import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import analytics from './analytics.js';
import './App.css';
import logo from './images/sea-bird.png';
import barsIcon from './images/food/bars.png';
import muffinIcon from './images/food/muffin.png';
import breadIcon from './images/food/bread.png';
import cakeIcon from './images/food/cake.png';
import cookieIcon from './images/food/cookie.png';
import quickBreadIcon from './images/food/quick_bread.png'; // Add icon for Quick Breads
import Footer from './Footer.js';
import { requestWakeLock, releaseWakeLock } from './wakeLock.js';
import allFoodIcon from './images/food/all_food.png';
import bakeryIcon from './images/food/bakery.png';
import restaurantIcon from './images/food/restaurant.png';
import pieIcon from './images/food/pie.png';
import menuIcon from './images/food/menu.jpg';
import { fetchWithCache } from './fetchWithCache.js';
import { RecipeListSkeleton } from './SkeletonLoaders.js';
import { RecipeGridSkeleton } from './RecipeGridSkeleton.js';

const API_URL = process.env.REACT_APP_API_URL;
console.log(`API URL from the Recipe List: ${API_URL}`);

function RecipeList() {
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [parentCategory, setParentCategory] = useState(null);
  const [grandParentCategory, setGrandParentCategory] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingRecipes, setIsLoadingRecipes] = useState(true);
  const recipeRefs = useRef({});
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    requestWakeLock();
    return () => {
      releaseWakeLock();
    };
  }, []);

  useEffect(() => {
    setIsLoadingCategories(true);
    fetchWithCache(`${API_URL}/api/food_categories`)
      .then(data => {
        setCategories(data.data);
        setIsLoadingCategories(false);
      })
      .catch(error => {
        console.error('There has been a problem with your fetch operation:', error);
        setIsLoadingCategories(false);
      });
  }, []);

  useEffect(() => {
    setIsLoadingRecipes(true);
    let url = `${API_URL}/api/recipes`;
    if (selectedCategory) {
      url += `?category_id=${selectedCategory}`;
    } else if (parentCategory) {
      url += `?parent_category_id=${parentCategory}`;
    } else if (grandParentCategory) {
      url += `?parent_category_id=${grandParentCategory}`;
    }

    // Bypass cache for fresh data with category_name
    fetch(url)
      .then(response => response.json())
      .then(data => {
        const sortedRecipes = data.data.sort((a, b) => a.recipe_name.localeCompare(b.recipe_name));
        setRecipes(sortedRecipes);
        setIsLoadingRecipes(false);
      })
      .catch(error => {
        console.error('There has been a problem with your fetch operation:', error);
        setIsLoadingRecipes(false);
      });
  }, [selectedCategory, parentCategory, grandParentCategory]);

  const getCategoryIcon = categoryName => {
    if (!categoryName) return allFoodIcon;
    
    switch (categoryName.toLowerCase()) {
      case 'bars': return barsIcon;
      case 'muffins': return muffinIcon;
      case 'bread': return breadIcon;
      case 'cake': return cakeIcon;
      case 'cookies': return cookieIcon;
      case 'pie': return pieIcon;
      case 'quick breads': return quickBreadIcon;
      case 'menu': return menuIcon;
      case 'all categories': return allFoodIcon;
      case 'bakery': return bakeryIcon;
      case 'restaurant': return restaurantIcon;
      case 'main dishes': return restaurantIcon;
      default: return allFoodIcon; // Default fallback icon
    }
  };

  const handleGrandParentCategoryClick = grandParent => {
    const categoryName = grandParent === null ? 'All Categories' : 
      categories.find(cat => cat.id === grandParent)?.name || 'Unknown';
    
    analytics.trackCategoryView(grandParent, categoryName);
    setGrandParentCategory(grandParent);
    setParentCategory(null);
    setSelectedCategory(null);
  };

  const handleParentCategoryClick = parent => {
    const categoryName = categories.find(cat => cat.id === parent)?.name || 'Unknown';
    analytics.trackCategoryView(parent, categoryName);
    setParentCategory(parent);
    setSelectedCategory(null);
  };

  const handleSubCategoryClick = subcategory => {
    const categoryName = categories.find(cat => cat.id === subcategory)?.name || 'Unknown';
    analytics.trackCategoryView(subcategory, categoryName);
    setSelectedCategory(subcategory);
  };

  const scrollToLetter = (letter) => {
    setSelectedLetter(letter);
    const element = recipeRefs.current[letter];
    if (element) {
      // Use a slight delay to ensure DOM is ready, then scroll with offset
      setTimeout(() => {
        const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
        const offset = 100; // Account for any sticky headers or spacing
        window.scrollTo({
          top: elementTop - offset,
          behavior: 'smooth'
        });
      }, 50);
    }
  };

  // Group recipes by first letter
  const groupedRecipes = recipes.reduce((acc, recipe) => {
    const firstLetter = recipe.recipe_name.charAt(0).toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(recipe);
    return acc;
  }, {});

  // Get all available letters from recipes
  const availableLetters = Object.keys(groupedRecipes).sort();
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const grandParentCategories = [
    { id: null, name: 'All Categories' },
    ...categories.filter(category => category.parent_id === null)
  ];

  const parentCategories = categories.filter(category => category.parent_id === grandParentCategory);
  const subCategories = categories.filter(category => category.parent_id === parentCategory);

  // Show skeleton loading while initial data is loading
  if ((isLoadingCategories && categories.length === 0) || (isLoadingRecipes && recipes.length === 0)) {
    return (
      <>
        <RecipeListSkeleton />
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="logo">
        <img src={logo} alt="logo" />
        <h1>The Seabird Cookbook</h1>
      </div>
      <div>
        <div className="categoryList">
          <h4>Categories</h4>
          <span className="categoryExtra"></span>
          <div className="categoryButtons">
            {grandParentCategories.map(grandParent => (
              <button
                key={grandParent.id}
                onClick={() => handleGrandParentCategoryClick(grandParent.id)}
                className={grandParentCategory === grandParent.id ? 'active' : ''}
              >
            <img src={getCategoryIcon(grandParent.name)} alt={grandParent.name} />
            {grandParent.name}
          </button>
            ))}
          </div>
          {grandParentCategory !== null && (
            <div className="subcategoryButtons">
              {parentCategories.map(parent => (
                <button
                  key={parent.id}
                  onClick={() => handleParentCategoryClick(parent.id)}
                  className={parentCategory === parent.id ? 'active' : ''}
                >
                  <img src={getCategoryIcon(parent.name)} alt={parent.name} />
                  {parent.name}
                </button>
              ))}
            </div>
          )}
          {parentCategory !== null && (
            <div className="subcategoryButtons">
              {subCategories.map(subcategory => (
                <button
                  key={subcategory.id}
                  onClick={() => handleSubCategoryClick(subcategory.id)}
                  className={selectedCategory === subcategory.id ? 'active' : ''}
                >
                  <img src={getCategoryIcon(subcategory.name)} alt={subcategory.name} />
                  {subcategory.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="recipe-list-container">
          {recipes.length > 0 && (
            <div className="alphabet-slider">
              {alphabet.map(letter => (
                <button
                  key={letter}
                  className={`alphabet-btn ${availableLetters.includes(letter) ? 'available' : 'disabled'} ${selectedLetter === letter ? 'active' : ''}`}
                  onClick={() => availableLetters.includes(letter) && scrollToLetter(letter)}
                  disabled={!availableLetters.includes(letter)}
                >
                  {letter}
                </button>
              ))}
            </div>
          )}
          
          <div className="recipeList">
            <h1>Recipes</h1>
            {isLoadingRecipes ? (
              <div className="letter-section">
                <div className="skeleton skeleton-text large" style={{ width: '30px', height: '32px', marginBottom: '20px' }}></div>
                <RecipeGridSkeleton />
              </div>
            ) : (
              availableLetters.map(letter => (
                <div 
                  key={letter} 
                  className="letter-section"
                  ref={el => recipeRefs.current[letter] = el}
                >
                  <h2 className="letter-header">
                    {letter}
                  </h2>
                  <div className="recipe-grid">
                    {groupedRecipes[letter].map(recipe => (
                      <div key={recipe.id} className="recipe-card">
                        <Link to={`/recipes/${recipe.id}`} className="recipe-link">
                          <div className="recipe-header">
                            <h3>{recipe.recipe_name}</h3>
                            <img 
                              src={getCategoryIcon(recipe.category_name)} 
                              alt={recipe.category_name || 'Recipe'}
                              className="recipe-category-icon"
                              title={recipe.category_name || 'Recipe'}
                            />
                          </div>
                          {recipe.recipe_description && (
                            <p className="recipe-description">{recipe.recipe_description}</p>
                          )}
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default RecipeList;